import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { WorkflowApiService, WorkflowIntentOpsListItem } from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-intent-ops-list',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TagModule,
    DatePipe
  ],
  template: `
    <ain-page [title]="t('workflow.intents.ops.title')" [description]="t('workflow.intents.ops.description')">
      <div class="toolbar">
        <button pButton type="button" [label]="t('workflow.intents.ops.actions.newIntent')" (click)="openCreate()"></button>
      </div>
      <div class="filters">
        <input pInputText [(ngModel)]="intentKey" [placeholder]="t('workflow.intents.ops.filters.intentKey')" />
        <p-select
          [options]="statusOptions"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="status"
          [placeholder]="t('workflow.intents.ops.filters.status')" />
        <p-select
          [options]="approvalOptions"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="approvalStatus"
          [placeholder]="t('workflow.intents.ops.filters.approvalStatus')" />
        <p-select
          [options]="executionOptions"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="executionStatus"
          [placeholder]="t('workflow.intents.ops.filters.executionStatus')" />
        <button pButton type="button" (click)="reload()" [label]="t('common.search')"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else {
        <p-table [value]="items()" [paginator]="true" [rows]="pageSize()" [totalRecords]="totalCount()"
                 [lazy]="true" (onLazyLoad)="onPage($event.first ?? 0, $event.rows ?? pageSize())">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('workflow.intents.ops.columns.intentKey') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('workflow.intents.ops.columns.approvalStatus') }}</th>
              <th>{{ t('workflow.intents.ops.columns.executionStatus') }}</th>
              <th>{{ t('workflow.intents.ops.columns.plannedAt') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.intentKey }}</td>
              <td><p-tag [value]="row.status"></p-tag></td>
              <td><p-tag [value]="row.approvalStatus" severity="warn"></p-tag></td>
              <td><p-tag [value]="row.executionStatus" severity="info"></p-tag></td>
              <td>{{ row.plannedAtUtc | date: 'short' }}</td>
              <td>
                <button pButton type="button" text [label]="t('common.open')" (click)="openDetail(row)"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .filters {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      margin-bottom: 16px;
      align-items: end;
    }
    .toolbar { display: flex; justify-content: flex-end; margin-bottom: 12px; }
  `]
})
export class WorkflowIntentOpsListComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly items = signal<WorkflowIntentOpsListItem[]>([]);
  readonly pageNumber = signal(1);
  readonly pageSize = signal(20);
  readonly totalCount = signal(0);

  status = '';
  approvalStatus = '';
  executionStatus = '';
  intentKey = '';

  get statusOptions() {
    return [
      { label: this.t('common.all'), value: '' },
      { label: 'Draft', value: 'Draft' },
      { label: 'Confirmed', value: 'Confirmed' },
      { label: 'Approved', value: 'Approved' },
      { label: 'Rejected', value: 'Rejected' },
      { label: 'Cancelled', value: 'Cancelled' }
    ];
  }

  get approvalOptions() {
    return [
      { label: this.t('common.all'), value: '' },
      { label: 'None', value: 'None' },
      { label: 'PendingApproval', value: 'PendingApproval' },
      { label: 'Approved', value: 'Approved' },
      { label: 'Rejected', value: 'Rejected' }
    ];
  }

  get executionOptions() {
    return [
      { label: this.t('common.all'), value: '' },
      { label: 'NotStarted', value: 'NotStarted' },
      { label: 'Queued', value: 'Queued' },
      { label: 'Started', value: 'Started' },
      { label: 'InProgress', value: 'InProgress' },
      { label: 'Completed', value: 'Completed' },
      { label: 'Failed', value: 'Failed' },
      { label: 'Deferred', value: 'Deferred' },
      { label: 'Cancelled', value: 'Cancelled' }
    ];
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listIntentOps({
      status: this.status || undefined,
      approvalStatus: this.approvalStatus || undefined,
      executionStatus: this.executionStatus || undefined,
      intentKey: this.intentKey || undefined,
      pageNumber: this.pageNumber(),
      pageSize: this.pageSize()
    }).subscribe({
      next: response => {
        this.items.set(response.items ?? []);
        this.totalCount.set(response.totalCount ?? 0);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(this.t('common.loadFailed'));
      }
    });
  }

  onPage(first: number, rows: number): void {
    this.pageNumber.set(Math.floor(first / rows) + 1);
    this.pageSize.set(rows);
    this.reload();
  }

  openDetail(item: WorkflowIntentOpsListItem): void {
    void this.router.navigate(['/workflow/intents', item.intentRequestId]);
  }

  openCreate(): void {
    void this.router.navigate(['/workflow/intents/new']);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
